/**
 * AvatarEditor Component
 *
 * Displays a user avatar with upload and delete controls.
 * Features a camera badge, hover overlay, and loading state.
 *
 * @module pages/SettingsPage/components/AvatarEditor
 */

import {
  type ChangeEvent,
  type ReactElement,
  memo,
  useCallback,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Loader, Text } from '@mantine/core';
import { Camera } from 'lucide-react';

import { UserAvatar } from '@/components/user/UserAvatar/UserAvatar';
import { NotificationService } from '@/services/notificationService';

import styles from './AvatarEditor.module.css';

const ACCEPTED_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
];
const ACCEPTED_IMAGE_TYPES_STRING = ACCEPTED_IMAGE_TYPES.join(',');
const MAX_AVATAR_SIZE_BYTES = 2 * 1024 * 1024;
const CAMERA_BADGE_ICON_SIZE = 14;
const CAMERA_OVERLAY_ICON_SIZE = 24;

interface AvatarEditorProps {
  avatarUrl: string | null;
  displayName: string;
  onUpload: (file: File) => void;
  onDelete: () => void;
  isUploading: boolean;
}

export const AvatarEditor = memo(function AvatarEditor({
  avatarUrl,
  displayName,
  onUpload,
  onDelete,
  isUploading,
}: AvatarEditorProps): ReactElement {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      // Reset input so the same file can be selected again
      event.target.value = '';

      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        NotificationService.showError(t('settings.profile.avatarInvalidType'));
        return;
      }

      if (file.size > MAX_AVATAR_SIZE_BYTES) {
        NotificationService.showError(t('settings.profile.avatarTooLarge'));
        return;
      }

      onUpload(file);
    },
    [onUpload, t]
  );

  return (
    <div className={styles.container}>
      <div
        className={styles.avatarWrapper}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        onKeyDown={(keyEvent) => {
          if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
            keyEvent.preventDefault();
            handleClick();
          }
        }}
      >
        <UserAvatar
          displayName={displayName}
          avatarUrl={avatarUrl}
          size="lg"
          className={styles.avatarOverride}
        />
        <div className={styles.cameraBadge}>
          <Camera size={CAMERA_BADGE_ICON_SIZE} />
        </div>
        <div className={styles.hoverOverlay}>
          <Camera size={CAMERA_OVERLAY_ICON_SIZE} />
        </div>
        {isUploading && (
          <div className={styles.loadingOverlay}>
            <Loader size="sm" color="white" />
          </div>
        )}
      </div>
      <div className={styles.controls}>
        {avatarUrl && (
          <Button variant="subtle" size="xs" color="red" onClick={onDelete}>
            {t('settings.profile.removeAvatar')}
          </Button>
        )}
        <Text size="xs" c="dimmed">
          {t('settings.profile.avatarHint')}
        </Text>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_IMAGE_TYPES_STRING}
        className={styles.hiddenInput}
        onChange={handleFileChange}
      />
    </div>
  );
});
